/**
 * Favorites Manager
 * Handles favorites/wishlist functionality with localStorage persistence
 * Includes add-to-cart and variant selection features (Jacquemus-style)
 */

const FAVORITES_STORAGE_KEY = 'vazquier_favorites';

class FavoritesManager {
  constructor() {
    this.favorites = this.loadFromStorage();
    this.drawer = null;
    this.toast = null;
    this.toastTimeout = null;
    this.productCache = {}; // Cache for product data
    this.selectedVariants = {}; // Track selected variants per product
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Setup drawer reference
    this.drawer = document.querySelector('favorites-drawer');
    this.toast = document.getElementById('favorites-toast');

    // Listen for favorite toggle clicks on product cards (capture phase to intercept before link)
    document.addEventListener('click', (event) => {
      const toggleButton = event.target.closest('[data-favorite-toggle]');
      if (toggleButton) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.handleToggleClick(toggleButton);
        return false;
      }
    }, true); // Use capture phase

    // Listen for other click events (bubbling phase)
    document.addEventListener('click', (event) => {
      // Listen for drawer open trigger
      const openTrigger = event.target.closest('[data-favorites-open]');
      if (openTrigger) {
        event.preventDefault();
        this.openDrawer();
      }

      // Listen for remove from favorites in drawer
      const removeButton = event.target.closest('[data-favorites-remove]');
      if (removeButton) {
        event.preventDefault();
        const productId = removeButton.dataset.productId;
        if (productId) {
          this.remove(productId);
        }
      }

      // Listen for add to cart from favorites
      const addToCartButton = event.target.closest('[data-favorites-add-to-cart]');
      if (addToCartButton) {
        event.preventDefault();
        const productId = addToCartButton.dataset.productId;
        if (productId) {
          this.addToCart(productId, addToCartButton);
        }
      }

      // Listen for add all to cart
      const addAllButton = event.target.closest('[data-favorites-add-all]');
      if (addAllButton) {
        event.preventDefault();
        this.addAllToCart(addAllButton);
      }

      // Listen for share wishlist
      const shareButton = event.target.closest('[data-favorites-share]');
      if (shareButton) {
        event.preventDefault();
        this.shareWishlist();
      }

      // Listen for adding shared item to own wishlist
      const addSharedButton = event.target.closest('[data-favorites-add-shared]');
      if (addSharedButton) {
        event.preventDefault();
        this.addSharedItemToWishlist(addSharedButton);
      }
    });

    // Check for shared wishlist in URL on load
    this.loadSharedWishlist();

    // Listen for variant selection changes
    document.addEventListener('change', (event) => {
      const variantSelect = event.target.closest('[data-favorites-variant-select]');
      if (variantSelect) {
        const productId = variantSelect.dataset.productId;
        const variantId = variantSelect.value;
        this.selectedVariants[productId] = variantId;
        this.updateItemPrice(productId, variantId);
      }
    });

    // Close drawer on escape key
    document.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE' && this.drawer?.classList.contains('is-visible')) {
        this.closeDrawer();
      }
    });

    // Initial UI sync
    this.updateUI();
  }

  handleToggleClick(button) {
    const productData = {
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      priceRaw: button.dataset.productPriceRaw || '0',
      url: button.dataset.productUrl || `/products/${button.dataset.productHandle}`,
      color: button.dataset.productColor || '',
      variantId: button.dataset.productVariantId || ''
    };

    this.toggle(productData);
  }

  // Core methods
  add(productData) {
    if (!productData.id) return false;
    
    // Check if already exists
    if (this.has(productData.id)) return false;

    this.favorites.push({
      id: String(productData.id),
      handle: productData.handle,
      title: productData.title,
      image: productData.image,
      price: productData.price,
      priceRaw: productData.priceRaw || '0',
      url: productData.url,
      color: productData.color || '',
      variantId: productData.variantId || '',
      addedAt: Date.now()
    });

    this.saveToStorage();
    this.updateUI();
    this.renderDrawerContents();
    return true;
  }

  remove(productId) {
    const id = String(productId);
    const index = this.favorites.findIndex(item => String(item.id) === id);
    
    if (index === -1) return false;

    this.favorites.splice(index, 1);
    delete this.selectedVariants[id];
    this.saveToStorage();
    this.updateUI();
    this.renderDrawerContents();
    return true;
  }

  toggle(productData) {
    if (this.has(productData.id)) {
      const result = this.remove(productData.id);
      if (result) {
        this.showToast(window.favoritesTranslations?.removed || 'The item has been removed from your favorites list.', false);
      }
      return result;
    } else {
      const result = this.add(productData);
      if (result) {
        this.showToast(window.favoritesTranslations?.added || 'The product is well added to your favorites.', true);
      }
      return result;
    }
  }

  has(productId) {
    const id = String(productId);
    return this.favorites.some(item => String(item.id) === id);
  }

  getAll() {
    return [...this.favorites];
  }

  getCount() {
    return this.favorites.length;
  }

  // Storage methods
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error loading favorites from storage:', e);
      return [];
    }
  }

  saveToStorage() {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(this.favorites));
    } catch (e) {
      console.error('Error saving favorites to storage:', e);
    }
  }

  // Fetch product data for variants
  async fetchProductData(handle) {
    if (this.productCache[handle]) {
      return this.productCache[handle];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');
      const product = await response.json();
      this.productCache[handle] = product;
      return product;
    } catch (e) {
      console.error('Error fetching product:', e);
      return null;
    }
  }

  // Cart methods
  async addToCart(productId, button) {
    const item = this.favorites.find(f => String(f.id) === String(productId));
    if (!item) return;

    // Get selected variant or default
    let variantId = this.selectedVariants[productId] || item.variantId;
    
    // If no variant selected, try to get the first available variant
    if (!variantId && item.handle) {
      const product = await this.fetchProductData(item.handle);
      if (product && product.variants && product.variants.length > 0) {
        const availableVariant = product.variants.find(v => v.available) || product.variants[0];
        variantId = availableVariant.id;
      }
    }

    if (!variantId) {
      console.error('No variant ID available for product:', productId);
      return;
    }

    // Update button state
    const originalText = button.textContent;
    button.textContent = window.favoritesTranslations?.adding || 'Adding...';
    button.disabled = true;

    // Get cart drawer for section rendering
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRender = cartDrawer?.getSectionsToRender?.() || [];
    const sectionIds = sectionsToRender.map(section => section.section);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sectionIds,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const responseData = await response.json();

      button.textContent = window.favoritesTranslations?.added_to_cart || 'Added!';
      
      // Update cart drawer with new contents and open it
      if (cartDrawer && responseData.sections) {
        cartDrawer.renderContents(responseData);
      }

      // Update cart counters
      if (typeof updateCartCounters === 'function') {
        updateCartCounters();
      }

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);

    } catch (e) {
      console.error('Error adding to cart:', e);
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  async addAllToCart(button) {
    const favorites = this.getAll();
    if (favorites.length === 0) return;

    // Update button state
    const originalText = button.textContent;
    button.textContent = window.favoritesTranslations?.adding || 'Adding...';
    button.disabled = true;

    const items = [];

    for (const item of favorites) {
      let variantId = this.selectedVariants[item.id] || item.variantId;
      
      // If no variant selected, try to get the first available variant
      if (!variantId && item.handle) {
        const product = await this.fetchProductData(item.handle);
        if (product && product.variants && product.variants.length > 0) {
          const availableVariant = product.variants.find(v => v.available) || product.variants[0];
          variantId = availableVariant.id;
        }
      }

      if (variantId) {
        items.push({
          id: variantId,
          quantity: 1
        });
      }
    }

    if (items.length === 0) {
      button.textContent = originalText;
      button.disabled = false;
      return;
    }

    // Get cart drawer for section rendering
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRender = cartDrawer?.getSectionsToRender?.() || [];
    const sectionIds = sectionsToRender.map(section => section.section);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          items,
          sections: sectionIds,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Failed to add items to cart');

      const responseData = await response.json();

      button.textContent = window.favoritesTranslations?.added_to_cart || 'Added!';
      
      // Update cart drawer with new contents and open it
      if (cartDrawer && responseData.sections) {
        cartDrawer.renderContents(responseData);
      }

      // Update cart counters
      if (typeof updateCartCounters === 'function') {
        updateCartCounters();
      }

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);

    } catch (e) {
      console.error('Error adding all to cart:', e);
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  // Calculate total price
  calculateTotal() {
    let total = 0;
    for (const item of this.favorites) {
      const priceRaw = parseInt(item.priceRaw) || 0;
      if (priceRaw > 0) {
        total += priceRaw;
      }
    }
    return total;
  }

  // Get display price - uses formatted price if raw is not available
  getDisplayPrice(item) {
    const rawPrice = parseInt(item.priceRaw);
    if (rawPrice && rawPrice > 0) {
      return this.formatPrice(rawPrice);
    }
    // Fallback to stored formatted price
    return item.price || '';
  }

  // Format price
  formatPrice(cents) {
    const amount = (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const currency = window.shopCurrency || 'EUR';
    return `${amount} ${currency}`;
  }

  // Update item price when variant changes
  async updateItemPrice(productId, variantId) {
    const item = this.favorites.find(f => String(f.id) === String(productId));
    if (!item || !item.handle) return;

    const product = await this.fetchProductData(item.handle);
    if (!product) return;

    const variant = product.variants.find(v => String(v.id) === String(variantId));
    if (!variant) return;

    // Update the price display
    const priceEl = this.drawer?.querySelector(`[data-favorite-item="${productId}"] .favorites-drawer__item-price`);
    if (priceEl) {
      priceEl.textContent = this.formatPrice(variant.price);
    }

    // Update stored price for total calculation
    item.priceRaw = String(variant.price);
    this.saveToStorage();
    this.updateFooter();
  }

  // Update footer with totals
  updateFooter() {
    if (!this.drawer) return;

    const footer = this.drawer.querySelector('[data-favorites-footer]');
    const itemsCountEl = this.drawer.querySelector('[data-favorites-items-count]');
    const totalPriceEl = this.drawer.querySelector('[data-favorites-total-price]');

    const count = this.getCount();

    if (count === 0) {
      if (footer) footer.classList.add('hidden');
      return;
    }

    if (footer) footer.classList.remove('hidden');

    // Update items count
    if (itemsCountEl) {
      const countText = (window.favoritesTranslations?.items_count || '{{count}} items')
        .replace('{{count}}', count);
      itemsCountEl.textContent = countText;
    }

    // Update total price
    if (totalPriceEl) {
      const total = this.calculateTotal();
      totalPriceEl.textContent = this.formatPrice(total);
    }
  }

  // Update header count display
  updateHeaderCount() {
    const countEl = this.drawer?.querySelector('[data-favorites-count]');
    const count = this.getCount();
    if (countEl) {
      countEl.textContent = count > 0 ? count : '';
    }
  }

  // UI methods
  updateUI() {
    // Update all favorite toggle buttons
    document.querySelectorAll('[data-favorite-toggle]').forEach(button => {
      const productId = button.dataset.productId;
      const isFavorite = this.has(productId);
      
      button.classList.toggle('is-favorited', isFavorite);
      button.setAttribute('aria-pressed', isFavorite);
      
      // Update aria-label
      const label = isFavorite 
        ? (window.favoritesTranslations?.remove || 'Remove from favorites')
        : (window.favoritesTranslations?.add || 'Add to favorites');
      button.setAttribute('aria-label', label);
    });

    // Update header button state (filled/empty heart)
    const count = this.getCount();
    document.querySelectorAll('[data-favorites-header-btn]').forEach(btn => {
      btn.classList.toggle('has-favorites', count > 0);
    });

    // Update header count
    this.updateHeaderCount();
  }

  openDrawer() {
    if (!this.drawer) {
      this.drawer = document.querySelector('favorites-drawer');
    }
    
    if (this.drawer) {
      this.drawer.open();
    }
  }

  closeDrawer() {
    if (this.drawer) {
      this.drawer.close();
    }
  }

  showToast(message, showViewLink = true) {
    if (!this.toast) {
      this.toast = document.getElementById('favorites-toast');
    }
    if (!this.toast) return;

    // Clear any existing timeout
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    // Update toast content
    const messageEl = this.toast.querySelector('.favorites-toast__message');
    const actionEl = this.toast.querySelector('.favorites-toast__action');
    
    if (messageEl) {
      messageEl.textContent = message;
    }
    
    if (actionEl) {
      actionEl.classList.toggle('hidden', !showViewLink);
    }

    // Show toast
    this.toast.classList.add('is-visible');

    // Auto-hide after 3 seconds
    this.toastTimeout = setTimeout(() => {
      this.toast.classList.remove('is-visible');
    }, 3000);
  }

  async renderDrawerContents() {
    if (!this.drawer) return;

    const itemsContainer = this.drawer.querySelector('[data-favorites-items]');
    const emptyState = this.drawer.querySelector('[data-favorites-empty]');
    const filledState = this.drawer.querySelector('[data-favorites-filled]');
    const titleEl = this.drawer.querySelector('.favorites-drawer__title');

    if (!itemsContainer) return;

    // Check if we're displaying shared products
    const isShowingShared = this.sharedProducts && this.sharedProducts.length > 0;
    const itemsToShow = isShowingShared ? this.sharedProducts : this.getAll();

    // Update title for shared wishlist
    if (titleEl) {
      const baseTitle = window.favoritesTranslations?.title || 'Favorites';
      const sharedTitle = window.favoritesTranslations?.shared_wishlist || 'Shared Wishlist';
      titleEl.childNodes[0].textContent = isShowingShared ? sharedTitle : baseTitle;
    }

    if (itemsToShow.length === 0) {
      if (emptyState) emptyState.classList.remove('hidden');
      if (filledState) filledState.classList.add('hidden');
      itemsContainer.innerHTML = '';
      this.updateFooter();
      this.updateHeaderCount();
      this.updateShareButton();
      return;
    }

    if (emptyState) emptyState.classList.add('hidden');
    if (filledState) filledState.classList.remove('hidden');

    // Render items with product data for variants
    const itemsHtml = await Promise.all(
      itemsToShow.map(item => this.renderFavoriteItem(item, isShowingShared))
    );
    itemsContainer.innerHTML = itemsHtml.join('');

    // Update footer and share button
    this.updateFooter();
    this.updateHeaderCount();
    this.updateShareButton();

    // Clear shared products after rendering (user can add them to their own wishlist)
    if (isShowingShared) {
      // Don't clear immediately - let user interact first
    }
  }

  async renderFavoriteItem(item, isShared = false) {
    const removeLabel = window.favoritesTranslations?.remove || 'Remove';
    const addToCartLabel = window.favoritesTranslations?.add_to_cart || 'Add to cart';
    const addToFavoritesLabel = window.favoritesTranslations?.add || 'Add to favorites';
    const sizeLabel = window.favoritesTranslations?.size || 'Size';

    // Fetch product data for variants and to fill in missing data
    let sizeOptions = '';
    let product = null;
    let displayPrice = item.price || '';
    let itemColor = item.color || '';
    let needsUpdate = false;
    
    if (item.handle) {
      product = await this.fetchProductData(item.handle);
      
      if (product) {
        // Get the selected or first available variant
        const selectedVariantId = this.selectedVariants[item.id] || item.variantId || product.variants[0]?.id;
        const selectedVariant = product.variants.find(v => String(v.id) === String(selectedVariantId)) || product.variants[0];
        
        // Update price from product data if not set or invalid
        const storedPrice = parseInt(item.priceRaw);
        if (!storedPrice || storedPrice === 0) {
          if (selectedVariant) {
            item.priceRaw = String(selectedVariant.price);
            needsUpdate = true;
          }
        }
        
        // Update variant ID if not set
        if (!item.variantId && selectedVariant) {
          item.variantId = String(selectedVariant.id);
          needsUpdate = true;
        }
        
        // Update color if not set
        if (!item.color && selectedVariant && selectedVariant.option1) {
          item.color = selectedVariant.option1;
          itemColor = item.color;
          needsUpdate = true;
        }
        
        // Save updated data
        if (needsUpdate) {
          this.saveToStorage();
        }
        
        // Format price from raw value or product data
        const priceValue = parseInt(item.priceRaw) || (selectedVariant ? selectedVariant.price : 0);
        if (priceValue > 0) {
          displayPrice = this.formatPrice(priceValue);
        }
        
        // Build size options if product has multiple variants with size option
        if (product.variants && product.variants.length > 1) {
          const sizeOptionIndex = product.options.findIndex(opt => 
            opt.toLowerCase() === 'size' || opt.toLowerCase() === 'talla' || opt.toLowerCase() === 'taille'
          );
          
          if (sizeOptionIndex !== -1) {
            sizeOptions = `
              <div class="favorites-drawer__item-size">
                <select 
                  class="favorites-drawer__size-select"
                  data-favorites-variant-select
                  data-product-id="${item.id}"
                >
                  ${product.variants.map(variant => {
                    const sizeValue = variant.options[sizeOptionIndex] || variant.title;
                    const isSelected = String(variant.id) === String(selectedVariantId);
                    const isAvailable = variant.available;
                    return `
                      <option 
                        value="${variant.id}" 
                        ${isSelected ? 'selected' : ''}
                        ${!isAvailable ? 'disabled' : ''}
                      >
                        ${sizeLabel} ${sizeValue}${!isAvailable ? ' - Sold out' : ''}
                      </option>
                    `;
                  }).join('')}
                </select>
                <svg class="favorites-drawer__size-arrow" width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            `;
            
            // Set default selected variant
            if (!this.selectedVariants[item.id]) {
              this.selectedVariants[item.id] = selectedVariantId;
            }
          }
        }
      }
    } else {
      // No handle available, use stored price if valid
      const storedPrice = parseInt(item.priceRaw);
      if (storedPrice && storedPrice > 0) {
        displayPrice = this.formatPrice(storedPrice);
      }
    }

    // Get color display - hide if it's "Default Title"
    const colorDisplay = itemColor && itemColor.toLowerCase() !== 'default title' 
      ? `<span class="favorites-drawer__item-color">${itemColor}</span>` 
      : '';

    return `
      <div class="favorites-drawer__item" data-favorite-item="${item.id}">
        <a href="${item.url}" class="favorites-drawer__item-image-link">
          <div class="favorites-drawer__item-image">
            ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
          </div>
        </a>
        <div class="favorites-drawer__item-content">
          <div class="favorites-drawer__item-header">
            <a href="${item.url}" class="favorites-drawer__item-title-link">
              <h4 class="favorites-drawer__item-title">${item.title}</h4>
            </a>
            <span class="favorites-drawer__item-price">${displayPrice}</span>
          </div>
          <div class="favorites-drawer__item-options">
            ${colorDisplay}
            ${sizeOptions}
          </div>
          <div class="favorites-drawer__item-actions">
            <button 
              type="button" 
              class="favorites-drawer__item-add-to-cart"
              data-favorites-add-to-cart
              data-product-id="${item.id}"
            >
              ${addToCartLabel}
            </button>
            ${isShared ? `
              <button 
                type="button" 
                class="favorites-drawer__item-add-to-wishlist"
                data-favorites-add-shared
                data-product-id="${item.id}"
                data-product-handle="${item.handle}"
                data-product-title="${item.title}"
                data-product-image="${item.image || ''}"
                data-product-price="${item.price}"
                data-product-price-raw="${item.priceRaw}"
                data-product-url="${item.url}"
                data-product-color="${item.color || ''}"
                data-product-variant-id="${item.variantId || ''}"
              >
                ${addToFavoritesLabel}
              </button>
            ` : `
              <button 
                type="button" 
                class="favorites-drawer__item-remove"
                data-favorites-remove
                data-product-id="${item.id}"
              >
                ${removeLabel}
              </button>
            `}
          </div>
        </div>
      </div>
    `;
  }

  // ========================================
  // Share Wishlist Methods
  // ========================================

  // Generate shareable URL with product handles
  generateShareableUrl() {
    const handles = this.favorites
      .filter(item => item.handle)
      .map(item => item.handle);
    
    if (handles.length === 0) return null;

    const baseUrl = window.shopUrl || window.location.origin;
    const params = new URLSearchParams();
    params.set('wishlist', handles.join(','));
    
    return `${baseUrl}?${params.toString()}`;
  }

  // Generate share text with product titles
  generateShareText() {
    const titles = this.favorites.map(item => item.title).filter(Boolean);
    const shareTitle = window.favoritesTranslations?.share_title || 'Check out my wishlist';
    
    if (titles.length === 0) return shareTitle;
    
    return `${shareTitle}:\n${titles.map(t => `• ${t}`).join('\n')}`;
  }

  // Share wishlist using Web Share API or clipboard fallback
  async shareWishlist() {
    const url = this.generateShareableUrl();
    if (!url) return;

    const shareText = this.generateShareText();
    const shareTitle = window.favoritesTranslations?.share_title || 'Check out my wishlist';

    // Try native Web Share API first (works on mobile and some desktop browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: url
        });
        return;
      } catch (e) {
        // User cancelled or share failed, fall back to clipboard
        if (e.name === 'AbortError') return;
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      this.showToast(window.favoritesTranslations?.link_copied || 'Link copied to clipboard!', false);
    } catch (e) {
      // Final fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.showToast(window.favoritesTranslations?.link_copied || 'Link copied to clipboard!', false);
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
      document.body.removeChild(textArea);
    }
  }

  // Load shared wishlist from URL parameters
  async loadSharedWishlist() {
    const urlParams = new URLSearchParams(window.location.search);
    const wishlistParam = urlParams.get('wishlist');
    
    if (!wishlistParam) return;

    const handles = wishlistParam.split(',').filter(Boolean);
    if (handles.length === 0) return;

    // Fetch product data for each handle and add to temporary display
    const sharedProducts = [];
    
    for (const handle of handles) {
      const product = await this.fetchProductData(handle);
      if (product) {
        const variant = product.variants[0];
        sharedProducts.push({
          id: String(product.id),
          handle: product.handle,
          title: product.title,
          image: product.featured_image || (product.images && product.images[0]),
          price: this.formatPrice(variant?.price || 0),
          priceRaw: String(variant?.price || 0),
          url: `/products/${product.handle}`,
          color: variant?.option1 || '',
          variantId: String(variant?.id || ''),
          isShared: true
        });
      }
    }

    if (sharedProducts.length > 0) {
      // Store shared products temporarily and open drawer
      this.sharedProducts = sharedProducts;
      this.openDrawer();
      
      // Clean URL without reloading
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }

  // Update share button visibility
  updateShareButton() {
    const shareButton = this.drawer?.querySelector('[data-favorites-share]');
    if (shareButton) {
      const hasItems = this.getCount() > 0 || (this.sharedProducts && this.sharedProducts.length > 0);
      shareButton.classList.toggle('hidden', !hasItems);
    }
  }

  // Add shared item to user's own wishlist
  addSharedItemToWishlist(button) {
    const productData = {
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      priceRaw: button.dataset.productPriceRaw || '0',
      url: button.dataset.productUrl,
      color: button.dataset.productColor || '',
      variantId: button.dataset.productVariantId || ''
    };

    // Check if already in favorites
    const alreadyExists = this.has(productData.id);
    const addedText = window.favoritesTranslations?.added_to_cart || 'Added!';
    
    if (alreadyExists) {
      // Item already in favorites - just update button state
      button.textContent = addedText;
      button.disabled = true;
      button.classList.add('is-added');
    } else {
      // Add to favorites
      const added = this.add(productData);
      
      if (added) {
        // Update button to show it's been added
        button.textContent = addedText;
        button.disabled = true;
        button.classList.add('is-added');
        
        this.showToast(window.favoritesTranslations?.added || 'The product is well added to your favorites.', false);
      }
    }
  }

  // Clear shared products and show user's own wishlist
  clearSharedProducts() {
    this.sharedProducts = null;
    this.renderDrawerContents();
  }
}

// Custom element for favorites drawer
class FavoritesDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
  }

  connectedCallback() {
    // Close button listener
    const closeButton = this.querySelector('[data-drawer-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close());
    }

    // Backdrop click listener
    this.addEventListener('click', (event) => {
      if (event.target === this || event.target.classList.contains('favorites-drawer__backdrop')) {
        this.close();
      }
    });
  }

  open(opener) {
    if (opener) this.activeElement = opener;
    
    this.classList.add('is-visible');
    // overflow:hidden on body does not hold on iOS: the page keeps scrolling
    // and drags the fixed panel with it. Same lock the modal dialog uses.
    bodyScroll.lock(this.querySelector('.favorites-drawer__inner'));
    
    // Render contents when opening
    if (window.favoritesManager) {
      window.favoritesManager.renderDrawerContents();
    }

    // Focus management
    this.addEventListener('transitionend', () => {
      const focusElement = this.querySelector('[data-drawer-close]');
      if (focusElement) focusElement.focus();
    }, { once: true });
  }

  close() {
    this.classList.remove('is-visible');
    bodyScroll.unlock(this.querySelector('.favorites-drawer__inner'));
    
    // Clear shared products when closing drawer
    if (window.favoritesManager) {
      window.favoritesManager.sharedProducts = null;
    }
    
    // Return focus to opener
    if (this.activeElement) {
      this.activeElement.focus();
      this.activeElement = null;
    }
  }
}

// Define custom element
if (!customElements.get('favorites-drawer')) {
  customElements.define('favorites-drawer', FavoritesDrawer);
}

// Initialize global instance
window.favoritesManager = new FavoritesManager();
